import { codecForType, defaultMatchValue } from "./codecs";
import { resolveOperator } from "./operators";
import { parseFilterSearch } from "./parse-filter-search";
import type {
  FilterFieldsConfig,
  FilterMatchOperator,
  FilterOp,
} from "./types";

export interface BuildMatcherInput<
  TFilters extends Record<string, unknown> = Record<string, unknown>,
> {
  fields: FilterFieldsConfig;
  search?:
    | {
        /** The route's raw `filters`, operator brackets included — parsed here. */
        filters?: Partial<TFilters>;
      }
    | undefined;
  savedView?: Partial<TFilters> | undefined;
}

/**
 * Wire operator → client predicate. The exclusion operators both collapse to
 * `isNot`, which is already set-based (`!values.includes(...)`), so it covers
 * `notIn` as well as `neq`. The comparison and range operators have no
 * client-predicate form — a field using them declares its own `matchValue`.
 */
const MATCH_OPERATOR_BY_OP: Partial<Record<FilterOp, FilterMatchOperator>> = {
  eq: "is",
  neq: "isNot",
  in: "isAnyOf",
  notIn: "isNot",
};

function firstDefined(...values: unknown[]): unknown {
  for (const value of values) {
    if (value !== undefined) return value;
  }
  return undefined;
}

/**
 * Client fold — PURE. Folds the active fields through `field.matchValue` (or
 * the codec's `defaultMatchValue` from `accessor`) into a single AND predicate.
 * An empty filter set matches everything (no predicates → `every` is `true`).
 * No React, no I/O — the caller applies it with `items.filter(matcher)`.
 */
export function buildMatcher<TItem>(
  input: BuildMatcherInput,
): (item: TItem) => boolean {
  const { fields, search, savedView } = input;
  const { filters: urlFilters, filtersOp: urlOperators } = parseFilterSearch(
    search?.filters,
  );

  const predicates: Array<(item: TItem) => boolean> = [];
  for (const field of fields) {
    const codec = codecForType[field.type];
    const effective = firstDefined(
      urlFilters[field.key],
      savedView?.[field.key],
    );
    if (codec.isEmptyValue(effective)) continue;
    const operator =
      field.operator ??
      MATCH_OPERATOR_BY_OP[
        resolveOperator(field, urlOperators[field.key], effective)
      ] ??
      "isAnyOf";
    if (field.matchValue) {
      predicates.push((item) => field.matchValue!(item, effective, operator));
    } else if (field.accessor) {
      predicates.push((item) =>
        defaultMatchValue(field.accessor!(item), effective, operator),
      );
    }
  }

  return (item: TItem) => predicates.every((predicate) => predicate(item));
}
